import React, { useMemo } from 'react';
import T from 'components/T';
import { M } from '@dashboard-experience/mastodon';
import { FieldConfiguration, Verification } from 'api/verifications/types';
import SecurityNotice from 'components/Verification/SecurityNotice';
import { JURISDICTIONS_MAPPING } from './mvrMatrixConstants';
import 'scss/MvrMatrixDocumentUpload.scss';

const INFO_HEADER_TRANSLATION_PREFIX =
  'components.Verification.MvrDocumentCollection.InformationHeader';

export const getDocStatusDisplay = (
  config: MvrFieldConfiguration | undefined,
): {
  docStatus: string;
  docStatusDescription: string;
} => {
  const docStatus = config?.documentStatus ?? 'missing';
  const docStatusDescription = config?.statusDescription || '';
  const effectiveDocStatus =
    docStatus !== 'missing' && !docStatusDescription
      ? 'document_rejected_default'
      : docStatus;

  return { docStatus: effectiveDocStatus, docStatusDescription };
};

const translationKey = (docStatus: string, suffix: string) =>
  `${INFO_HEADER_TRANSLATION_PREFIX}.${docStatus}.${suffix}`;

const MvrDocumentCollectionInfoHeader = ({
  verification,
}: MvrDocumentCollectionInfoHeaderProps) => {
  const mvrFieldConfiguration = verification
    .fieldConfigurations[0] as MvrFieldConfiguration;

  const { docStatus, docStatusDescription } = useMemo(
    () => getDocStatusDisplay(mvrFieldConfiguration),
    [mvrFieldConfiguration],
  );

  const jurisdiction = mvrFieldConfiguration?.jurisdiction ?? '';
  const jurisdictionDisplayName =
    JURISDICTIONS_MAPPING[jurisdiction] ?? jurisdiction;

  return (
    <M.Grid>
      <M.Container>
        <M.GridRow>
          <M.GridCol lg={16}>
            <h2 data-testid='information-header'>
              <T value={translationKey(docStatus, 'title')} />
            </h2>
          </M.GridCol>
        </M.GridRow>
        {docStatus !== 'missing' && (
          <>
            <br />
            <M.GridRow>
              <M.GridCol>
                <p className='info-header-details'>
                  <T
                    value={translationKey(docStatus, 'details')}
                    jurisdiction={jurisdictionDisplayName}
                  />
                </p>
                {docStatusDescription && (
                  <p
                    className='info-header-issue-found'
                    data-testid='issue-found'
                  >
                    <strong>
                      <T value={translationKey(docStatus, 'issueFoundLabel')} />
                    </strong>{' '}
                    {docStatusDescription}
                  </p>
                )}
              </M.GridCol>
            </M.GridRow>
          </>
        )}
        <br />
        <M.GridRow>
          <SecurityNotice logoHeight='32px' smallerTitle />
        </M.GridRow>
      </M.Container>
    </M.Grid>
  );
};

export interface MvrFieldConfiguration extends FieldConfiguration {
  documentStatus?: string;
  statusDescription?: string;
  jurisdiction?: string;
}
interface MvrDocumentCollectionInfoHeaderProps {
  readonly verification: Verification;
}

export default MvrDocumentCollectionInfoHeader;
